
import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

interface PhotoUploadProps {
  value: (File | string)[];
  onChange: (value: (File | string)[]) => void;
}

const PhotoUpload = ({ 
  value, 
  onChange 
}: PhotoUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Create preview URLs for display
    const urls: string[] = [];
    
    value.forEach(file => {
      if (typeof file === 'string') {
        urls.push(file);
      } else {
        const url = URL.createObjectURL(file);
        urls.push(url);
      }
    });
    
    setPreviewUrls(urls);
    
    // Clean up URLs when component unmounts or values change
    return () => {
      urls.forEach(url => {
        if (!url.startsWith('http')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [value]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const fileArray = Array.from(e.target.files);
      onChange([...fileArray, ...value.filter(file => typeof file === 'string')]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const fileArray = Array.from(e.dataTransfer.files);
      onChange([...fileArray, ...value.filter(file => typeof file === 'string')]);
    }
  };
  
  const handleRemove = (index: number) => {
    const newValue = [...value];
    newValue.splice(index, 1);
    onChange(newValue);
  };

  return (
    <div>
      <div
        className="border-2 border-dashed rounded-md p-4 flex flex-col items-center justify-center cursor-pointer"
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
          ref={fileInputRef}
        />
        {uploading ? (
          <div>Uploading...</div>
        ) : (
          <div>
            <p className="text-sm text-gray-500">
              Glissez-déposez des images ici ou cliquez pour sélectionner
            </p>
          </div>
        )}
      </div>

      {previewUrls.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-4">
          {previewUrls.map((url, index) => (
            <div key={index} className="relative">
              <img
                src={url}
                alt={`Preview ${index}`}
                className="rounded-md object-cover aspect-square w-full"
              />
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 bg-black/20 text-white hover:bg-black/50"
                onClick={() => handleRemove(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
